import { ApiTransport } from "../../../network/ApiTransport";
import { MockApiTransport } from "../../../network/MockApiTransport";
import { configHelper } from "../../../config/ConfigHelper";
import type { IApiTransport } from "../../../definitions/IApiTransport";
import type { Instrument } from "../definitions/Instrument";
import { mockInstrumentCatalog } from "../mocks/instrumentCatalog.fixture";

/** `?mock=1` swaps in `MockApiTransport` so the dashboard works offline / while the real API is unavailable. */
function isMockEnabled(): boolean {
  return new URLSearchParams(window.location.search).get("mock") === "1";
}

const mockEnabled = isMockEnabled();
const apiTransport: IApiTransport = mockEnabled
  ? new MockApiTransport(mockInstrumentCatalog)
  : new ApiTransport();

function isInstrument(value: unknown): value is Instrument {
  if (typeof value !== "object" || value === null) return false;
  const v = value as { symbol?: unknown; name?: unknown; group?: unknown };
  return typeof v.symbol === "string" && typeof v.name === "string" && typeof v.group === "string";
}

/**
 * The real `.../instruments/depths` response — a much bigger, venue-specific
 * blob (~200 instruments' full trading config + live depth ladders). Only
 * `instruments.<symbol>.{canonical_symbol,name,_group,_rank}` is used here;
 * the rest (margin, trading hours, the sibling `depths` order-book
 * snapshot, ...) isn't needed for a symbol-picker dropdown. `_rank` is the
 * venue's own notion of instrument prominence (1 = most prominent, e.g.
 * EURUSD) — see `parseInstrumentCatalog`'s sort below.
 */
interface DepthsResponse {
  instruments: Record<
    string,
    { canonical_symbol: string; name: string; _group?: string; _rank?: number }
  >;
}

function isDepthsResponse(value: unknown): value is DepthsResponse {
  if (typeof value !== "object" || value === null || !("instruments" in value)) return false;
  const { instruments } = value as { instruments: unknown };
  if (typeof instruments !== "object" || instruments === null) return false;
  return Object.values(instruments).every(
    (i) =>
      typeof i === "object" &&
      i !== null &&
      typeof (i as { canonical_symbol?: unknown }).canonical_symbol === "string" &&
      typeof (i as { name?: unknown }).name === "string",
  );
}

/** Accepts either the fixture's flat `Instrument[]` or the real `.../depths` envelope. */
function parseInstrumentCatalog(raw: unknown, url: string): Instrument[] {
  if (Array.isArray(raw) && raw.every(isInstrument)) {
    return raw;
  }
  if (isDepthsResponse(raw)) {
    return Object.values(raw.instruments)
      .map((i) => ({
        symbol: i.canonical_symbol,
        name: i.name,
        group: i._group ?? "Other",
        rank: i._rank ?? Infinity,
      }))
      // Rank order (the venue's own notion of prominence — 1 = EURUSD-type
      // majors) rather than alphabetical: alphabetical put things like
      // "AAVEUSD"/"Aptos"/"Arbitrum" ahead of EUR/USD, GBP/USD, ... in the
      // dropdown, which is backwards for a picker most people reach for a
      // major pair in. Unranked instruments (no `_rank`) sort last, then by
      // symbol among themselves for a stable order.
      .sort((a, b) => a.rank - b.rank || a.symbol.localeCompare(b.symbol))
      .map(({ symbol, name, group }): Instrument => ({ symbol, name, group }));
  }
  throw new Error(`Unexpected instrument catalog response shape from ${url}`);
}

async function fetchInstrumentCatalogUncached(venueKey: string): Promise<Instrument[]> {
  const url = configHelper.getVenueDepthsUrl(venueKey);
  if (!url) {
    throw new Error(`No instrument-depths endpoint configured for venue "${venueKey}"`);
  }
  const headers = configHelper.getVenueDepthsHeaders(venueKey);
  const raw = await apiTransport.makeAPICall<unknown>(url, apiTransport.getOptions("GET", headers));
  return parseInstrumentCatalog(raw, url);
}

/**
 * Module-level cache, keyed by venue — shared by *every* caller, not just
 * one chart instance. `~200` instruments per venue is a big-enough payload
 * that two chart components both wanting `BROKT1`'s catalog (or one
 * component's user closing and reopening the same dropdown) shouldn't each
 * pay for their own fetch. Caches the in-flight `Promise`, not just the
 * resolved value, so concurrent callers during the first fetch also
 * dedupe onto the same request instead of each firing their own. A failed
 * fetch evicts itself so the next call retries rather than staying broken
 * for the rest of the page's lifetime.
 */
const catalogCache = new Map<string, Promise<Instrument[]>>();

/**
 * Fetches a venue's instrument catalog — used to populate a compare-
 * instrument dropdown (see `<lmax-web-spread-chart>`'s "+" tiles and symbol
 * chip). Sourced from `venues[venueKey].endpoint` in `config.json`
 * (`ConfigHelper.getVenueDepthsUrl`/`getVenueDepthsHeaders`); throws if
 * that venue has no `endpoint` configured. Cached per venue for the
 * lifetime of the page — see `catalogCache` above.
 */
export function fetchInstrumentCatalog(venueKey: string): Promise<Instrument[]> {
  const cached = catalogCache.get(venueKey);
  if (cached) return cached;

  const request = fetchInstrumentCatalogUncached(venueKey);
  request.catch(() => catalogCache.delete(venueKey));
  catalogCache.set(venueKey, request);
  return request;
}
